import { formatNumber } from '../number/format';
import requestIdleCallback from '../shims/requestIdleCallback';
import newValidator from '../validation';

const MortgageCalculator = function( form: HTMLFormElement ) {
    this.form = form;
    this.rate = this.form.dataset.rate;
    this.isLoading = false;

    this.validator = newValidator( form );

    this.fields = this.form.getElementsByTagName( 'input' ) as HTMLCollectionOf<HTMLInputElement>;
    this.select = this.form.querySelector( '.interest-rate-type' );
    this.fieldSet = this.form.querySelector( 'fieldset' );

    this.result = this.form.querySelector( '.result' );
    this.taxesAndFees = this.form.querySelector( '.taxesAndFees' );
    this.taxes = this.form.querySelector( '.taxes' );
    this.fees = this.form.querySelector( '.fees' );

    this.error = this.form.querySelector( '.error-container' );

    this.btn = this.form.querySelector( '.vendor-link' );

    if ( this.btn ) {
        this.btn.addEventListener( 'click', this.handleVendorClick.bind( this ) );
    }

    this.interestRateVariableResident = parseFloat( this.form.dataset.interestRateVariableResident );
    this.interestRateVariableNonResident = parseFloat( this.form.dataset.interestRateVariableNonResident );
    this.interestRateFixedResident = parseFloat( this.form.dataset.interestRateFixedResident );
    this.interestRateFixedNonResident = parseFloat( this.form.dataset.interestRateFixedNonResident );
    this.repaymentTermResident = parseInt( this.form.dataset.repaymentTermResident );
    this.repaymentTermNonResident = parseInt( this.form.dataset.repaymentTermNonResident );
    this.depositPercentageResident = parseFloat( this.form.dataset.depositPercentageResident );
    this.depositPercentageNonResident = parseFloat( this.form.dataset.depositPercentageNonResident );

    this.state = {
        fees: parseFloat( this.form.dataset.fees ),
        taxes: parseFloat( this.form.dataset.taxes ),
        resident: this.form.dataset.resident === 'true',
        interestRateType: this.select.value,
        taxRate: parseFloat( this.form.dataset.regionalRate ),
    };

    for ( let i = 0; i < this.fields.length; i++ ) {
        if ( this.fields[ i ].name === 'resident' ) {
            this.fields[ i ].addEventListener( 'input', this.handleResidencyChange.bind( this ) );
            continue;
        }

        this.fields[ i ].addEventListener( 'input', this.handleInput.bind( this ) );

        if ( this.fields[ i ].type === 'number' ) {
            this.state[ this.fields[ i ].name ] = this.fields[ i ].valueAsNumber;
        } else {
            this.state[ this.fields[ i ].name ] = this.fields[ i ].value || false;
        }
    }

    this.select.addEventListener( 'input', this.handlesInterestTypeChange.bind( this ) );

    form.dataset.initialised = 'true';
};

MortgageCalculator.prototype.handleResidencyChange = function( event: Event ): void {
    const target = event.target as HTMLInputElement;
    const resident = target.value === 'yes';

    const value = parseFloat( this.state.propertyPrice );
    const amount = resident ? this.depositPercentageResident : this.depositPercentageNonResident;
    const deposit = ( value / 100 ) * amount;

    this.updateState( 'deposit', parseFloat( deposit.toFixed( 0 ) ) );

    const repaymentTerm = resident ? this.repaymentTermResident : this.repaymentTermNonResident;
    this.updateState( 'repaymentTerm', repaymentTerm );

    this.updateState( 'resident', resident );

    this.update();
};

MortgageCalculator.prototype.handlesInterestTypeChange = function( event: Event ): void {
    const target = event.target as HTMLSelectElement;
    this.updateState( target.name, target.value );
    this.update();
};

MortgageCalculator.prototype.handleInput = function( event: Event ): void {
    const target = event.target as HTMLInputElement;
    this.updateState( target.name, target.valueAsNumber );
    this.update();
};

MortgageCalculator.prototype.updateRates = function(): void {
    let interestRate: number = 0;

    if ( this.state.interestRateType === 'variable' ) {
        if ( this.state.resident ) {
            interestRate = this.interestRateVariableResident;
        } else {
            interestRate = this.interestRateVariableNonResident;
        }
    } else {
        if ( this.state.resident ) {
            interestRate = this.interestRateFixedResident;
        } else {
            interestRate = this.interestRateFixedNonResident;
        }
    }

    this.updateState( 'interestRate', interestRate );
    this.update();
};

MortgageCalculator.prototype.updateState = function( key: string, value: any ) {
    this.state[ key ] = value;

    if ( key === 'interestRateType' || key === 'resident' ) {
        this.updateRates();
        return;
    }

    for ( let i = 0; i < this.fields.length; i++ ) {
        if ( this.fields[ i ].name !== key ) {
            continue;
        }

        this.fields[ i ].value = value;
    }
};

MortgageCalculator.prototype.update = function(): void {
    if ( this.error.children.length > 0 ) {
        this.error.innerHTML = '';
    }

    this.validator.reset();

    if ( !this.validator.validate( null, true ) ) {
        this.validator.getErrors().forEach( ( error ) => {
            const errMsg = this.form.dataset[ `error${error.input.name[ 0 ].toUpperCase() + error.input.name.substring( 1 )}` ];
            this.error.insertAdjacentHTML( 'beforeend', `<p>${errMsg}</p>` );
        } );
        return;
    }

    const estimate = this.calculate();

    this.result.innerHTML = `${window.currencySymbol} ${formatNumber( estimate.price )}`;
    this.taxesAndFees.innerHTML = `${window.currencySymbol} ${formatNumber( estimate.taxes + estimate.fees )}`;
    this.taxes.innerHTML = `${window.currencySymbol} ${formatNumber( estimate.taxes )}`;
    this.fees.innerHTML = `${window.currencySymbol} ${formatNumber( estimate.fees )}`;
};

MortgageCalculator.prototype.calculate = function() {
    const state = this.state;

    const estimate = {
        price: 0,
        taxes: ( state.propertyPrice / 100 ) * state.taxRate,
        fees: state.fees,
    };

    if ( state.interestRate === 0 ) {
        estimate.price = ( state.propertyPrice / state.repaymentTerm ) / 12;
    } else {
        const mortgageAmount = state.propertyPrice - state.deposit;
        const monthlyInterestRate = ( state.interestRate / 100 ) / 12;
        const totalNumberOfPayments = state.repaymentTerm * 12;
        const onePlusRToN = Math.pow( ( 1 + monthlyInterestRate ), totalNumberOfPayments );
        estimate.price = Math.floor( ( monthlyInterestRate * mortgageAmount * onePlusRToN ) / ( onePlusRToN - 1 ) );
    }

    return estimate;
};

MortgageCalculator.prototype.handleVendorClick = function( event: Event ): void {
    window.open( ( event.target as HTMLElement ).dataset.url, '_blank' );
};

function init() {
    const form = document.querySelector<HTMLFormElement>( '.mortgage-calculator-form' );

    if ( !form || form.dataset.initialised ) {
        return;
    }

    new MortgageCalculator( form );
}

export default function initMortgageCalculator() {
    init();

    window.addEventListener( 'twc:popup:open-property', () => {
        requestIdleCallback( init );
    } );
}
